/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export const tableOfContents = [{
  "depth": 2,
  "value": "Background",
  "id": "background"
}, {
  "depth": 2,
  "value": "Notes",
  "id": "notes",
  "children": [{
    "depth": 3,
    "value": "Design Recipe",
    "id": "design-recipe"
  }, {
    "depth": 3,
    "value": "Following the Design Recipe",
    "id": "following-the-design-recipe"
  }, {
    "depth": 3,
    "value": "List Abstractions",
    "id": "list-abstractions"
  }, {
    "depth": 3,
    "value": "Local, Lambda",
    "id": "local-lambda"
  }, {
    "depth": 3,
    "value": "Multiple complex inputs",
    "id": "multiple-complex-inputs"
  }]
}, {
  "depth": 2,
  "value": "Linter",
  "id": "linter"
}];
export const slug = "cs2500";
export const title = "Fundamentals of CS 1";
export const subtitle = "Notes/tools for Northeastern's intro to CS class.";
export const date = "2019-10-26";
export const author = "Dylan Burati";
export const tags = ["Northeastern"];
function _createMdxContent(props) {
  const _components = Object.assign({
    p: "p",
    h2: "h2",
    a: "a",
    h3: "h3",
    ul: "ul",
    li: "li",
    ol: "ol",
    em: "em",
    code: "code",
    strong: "strong",
    pre: "pre",
    span: "span"
  }, props.components), {TableOfContents, FundiesLinter} = _components;
  if (!FundiesLinter) _missingMdxReference("FundiesLinter", true);
  if (!TableOfContents) _missingMdxReference("TableOfContents", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Notes/tools for Northeastern's intro to CS class."
    }), "\n", _jsx(_components.h2, {
      id: "background",
      children: "Background"
    }), "\n", _jsxs(_components.p, {
      children: ["Northeastern's computer science faculty decided at some point to rewrite their\nintroductory CS classes, to have a focus on designing programs over just writing\ncode. The course is taught in ", _jsx(_components.a, {
        href: "https://racket-lang.org",
        children: "Racket"
      }), ", or more specifically, the\n", _jsx(_components.a, {
        href: "https://felleisen.org/matthias/OnHtDP/Teaching_Languages___SL___Not_Racket.html",
        children: "\"Teaching Languages\""
      }), ", which are subsets of Racket that limit\ncertain language features and promote test-driven development."]
    }), "\n", _jsx(TableOfContents, {}), "\n", _jsx(_components.h2, {
      id: "notes",
      children: "Notes"
    }), "\n", _jsx(_components.h3, {
      id: "design-recipe",
      children: "Design Recipe"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Functions", "\n", _jsxs(_components.ol, {
          children: ["\n", _jsx(_components.li, {
            children: "Write a signature"
          }), "\n", _jsx(_components.li, {
            children: "Write a purpose statement"
          }), "\n", _jsx(_components.li, {
            children: "Write at least 2 tests*"
          }), "\n", _jsx(_components.li, {
            children: "Write the code"
          }), "\n"]
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Tests have to cover every edge case -- common ones include the empty list, empty\nstring, negative numbers or zero, etc. If the data type is an enumeration or\nunion, the tests should cover each enumeration value or subtype."
    }), "\n", _jsxs(_components.p, {
      children: ["It is not possible to test functions that ", _jsx(_components.em, {
        children: "call"
      }), " ", _jsx(_components.code, {
        children: "big-bang"
      }), " with the tools we\nhave, because the return value is affected by user input. The functions which\nhandle events from ", _jsx(_components.code, {
        children: "big-bang"
      }), " should be tested like any other function.\nFunctions which call ", _jsx(_components.code, {
        children: "random"
      }), " should be checked with ", _jsx(_components.code, {
        children: "check-random"
      }), ", and\nfunctions which may return inexact numbers have ", _jsx(_components.code, {
        children: "check-within"
      }), " tests."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Data 0. Complete this step above the function if you use a type that isn't\nwell-known (not needed for String, Int, PositiveReal, Boolean, etc.)", "\n", _jsxs(_components.ol, {
          children: ["\n", _jsx(_components.li, {
            children: "Write a data definition stating the type name and what data it can hold"
          }), "\n", _jsx(_components.li, {
            children: "Write an interpretation statement for the type"
          }), "\n", _jsx(_components.li, {
            children: "Write some constants that have the type (these are used later for testing)"
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.strong, {
              children: "Write a template that branches, deconstructs, and/or recurses through\nthis type of data, calling templates when encountering other designed\ntypes"
            })
          }), "\n"]
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Example:"
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        className: "hljs language-scheme",
        children: [_jsx(_components.span, {
          className: "hljs-comment",
          children: "; A Side is one of:"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - \"left\""
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - \"center\""
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - \"right\""
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; and represents the horizontal position of one thing relative to another"
        }), "\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " SIDE-LEFT ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"left\""
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " SIDE-CENTER ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"center\""
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " SIDE-RIGHT ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"right\""
        }), ")\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "side-temp"
        }), " side)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n    (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n      [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "string=?"
          })
        }), " side SIDE-LEFT) ...]\n      [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "string=?"
          })
        }), " side SIDE-CENTER) ...]\n      [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "string=?"
          })
        }), " side SIDE-RIGHT) ...])))\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "define-struct"
        }), " weight [", _jsx(_components.span, {
          className: "hljs-name",
          children: "side"
        }), " mass])\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; A Weight is a (make-weight Side NonNegativeReal)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; and represents a weight on a see-saw"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - the side of the see-saw it is on"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - its mass in kilograms"
        }), "\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " WEIGHT-0 (", _jsx(_components.span, {
          className: "hljs-name",
          children: "make-weight"
        }), " SIDE-RIGHT ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0.0"
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " WEIGHT-1 (", _jsx(_components.span, {
          className: "hljs-name",
          children: "make-weight"
        }), " SIDE-LEFT ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1.0"
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " WEIGHT-2 (", _jsx(_components.span, {
          className: "hljs-name",
          children: "make-weight"
        }), " SIDE-CENTER ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2.0"
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " WEIGHT-3 (", _jsx(_components.span, {
          className: "hljs-name",
          children: "make-weight"
        }), " SIDE-RIGHT ", _jsx(_components.span, {
          className: "hljs-number",
          children: "3.0"
        }), "))\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-temp"
        }), " w)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "side-temp"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-side"
        }), " w))\n   ... (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-mass"
        }), " w)))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; A SeeSaw is one of:"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - empty"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - (cons Weight SeeSaw)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; and represents either a see-saw with one or more weights"
        }), "\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " SEESAW-0 empty)\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " SEESAW-1 (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cons"
          })
        }), " WEIGHT-1 SEESAW-0))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " SEESAW-2 (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cons"
          })
        }), " WEIGHT-2 SEESAW-1))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " SEESAW-3 (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cons"
          })
        }), " WEIGHT-3 SEESAW-2))\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-temp"
        }), " lst)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n    (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n      [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "empty?"
        }), " los) ...]\n      [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "cons?"
        }), " los)\n        (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n          (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-temp"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " lst))\n          (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-temp"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " lst)))])))\n"]
      })
    }), "\n", _jsx(_components.h3, {
      id: "following-the-design-recipe",
      children: "Following the Design Recipe"
    }), "\n", _jsx(_components.p, {
      children: "If the template calls another template, it indicates that the function using the\ntemplate needs another function to do work on the other data type. Other indicators\nare long and nested parts of the function, repeated selectors, and if statements\ndeciding between similar recursive calls."
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        className: "hljs language-scheme",
        children: ["(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " ACCEL-GRAVITY ", _jsx(_components.span, {
          className: "hljs-number",
          children: "9.8"
        }), ")\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; seesaw-torque : SeeSaw PosNumber -> Number"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Determines the torque in newton-meters on a see-saw with the given length in meters"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-torque"
        }), " SEESAW-0 ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-torque"
        }), " SEESAW-1 ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "9.8"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-torque"
        }), " SEESAW-2 ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "9.8"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-torque"
        }), " SEESAW-3 ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "-19.6"
        }), ")\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; bad example"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (define (seesaw-torque lst len)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";   (cond"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";     [(empty? los) 0]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";     [(cons? los)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";      (+"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";       (*"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";         len"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";         -1"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";         (cond"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";           [(string=? (weight-side (first lst)) SIDE-LEFT) -0.5]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";           [(string=? (weight-side (first lst)) SIDE-CENTER) 0]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";           [(string=? (weight-side (first lst)) SIDE-RIGHT) 0.5])"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";         ACCEL-GRAVITY"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";         (weight-mass (first lst)))"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";       (seesaw-torque (rest lst) len))]))"
        }), "\n"]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Note: inlining an ", _jsx(_components.code, {
        children: "if"
      }), " returning 0 or 1 can be OK, but it's always preferred to\nuse helper functions."]
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        className: "hljs language-scheme",
        children: [_jsx(_components.span, {
          className: "hljs-comment",
          children: "; same tests"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-torque"
        }), " lst len)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "empty?"
        }), " los) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0"
        }), "]\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "cons?"
        }), " los)\n     (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "+"
          })
        }), "\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-torque"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " lst) len)\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: "seesaw-torque"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " lst) len))]))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; weight-torque : Weight PosNumber -> Number"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Returns the torque that this weight would exert on a see-saw with the given length"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; in meters. The fulcrum of the see-saw is at its center, and gravity is 9.8 m/s^2."
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Weights on the left will generate positive, counter-clockwise torque"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-torque"
        }), " WEIGHT-0) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-torque"
        }), " WEIGHT-1) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "9.8"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-torque"
        }), " WEIGHT-2) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-torque"
        }), " WEIGHT-3) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "-29.4"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-torque"
        }), " w len)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "*"
          })
        }), "\n    len\n    ", _jsx(_components.span, {
          className: "hljs-number",
          children: "-1"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "dist-to-center"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-side"
        }), " w))\n    ACCEL-GRAVITY\n    (", _jsx(_components.span, {
          className: "hljs-name",
          children: "weight-mass"
        }), " w)))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; dist-to-center : Side -> Number"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Returns the distance of the given side to the center position."
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "dist-to-center"
        }), " SIDE-LEFT) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "-0.5"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "dist-to-center"
        }), " SIDE-CENTER) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "dist-to-center"
        }), " SIDE-RIGHT) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0.5"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "dist-to-center"
        }), " side)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "string=?"
          })
        }), " side SIDE-LEFT) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "-0.5"
        }), "]\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "string=?"
          })
        }), " side SIDE-CENTER) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0"
        }), "]\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "string=?"
          })
        }), " side SIDE-RIGHT) ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0.5"
        }), "]))\n"]
      })
    }), "\n", _jsx(_components.h3, {
      id: "list-abstractions",
      children: "List Abstractions"
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        className: "hljs language-scheme",
        children: [_jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; (length (build-list n ??)) === n"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X) NatNum [NatNum -> X] -> [List-of X]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; constructs a list by applying f to 0, 1, ..., (sub1 n)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (build-list n f) == (list (f 0) ... (f (- n 1)))"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "build-list"
        }), " n f) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; (length (filter p lx)) <= (length lx)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X) [X -> Boolean] [List-of X] -> [List-of X]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; produces a list from those items on lx for which p holds"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "filter"
        }), " p lx) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; elements are identical, but have known ordering in (sort lx)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X) [List-of X] [X X -> Boolean] -> [List-of X]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; produces a version of lx that is sorted according to cmp"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "sort"
        }), " lx cmp) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; (length (map f lx)) === (length lx)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X Y) [X -> Y] [List-of X] -> [List-of Y]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; constructs a list by applying f to each item on lx"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (map f (list x-1 ... x-n)) == (list (f x-1) ... (f x-n))"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "map"
          })
        }), " f lx) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; For all"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X) [X -> Boolean] [List-of X] -> Boolean"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; determines whether p holds for every item on lx"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (andmap p (list x-1 ... x-n)) == (and (p x-1) ... (p x-n))"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "andmap"
        }), " p lx) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; There exists"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X) [X -> Boolean] [List-of X] -> Boolean"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; determines whether p holds for at least one item on lx"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (ormap p (list x-1 ... x-n)) == (or (p x-1) ... (p x-n))"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "ormap"
        }), " p lx) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X Y) [X Y -> Y] Y [List-of X] -> Y"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; applies f from right to left to each item in lx and b"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (foldr f b (list x-1 ... x-n)) == (f x-1 ... (f x-n b))"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "foldr"
        }), " f b lx) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (X Y) [X Y -> Y] Y [List-of X] -> Y"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; applies f from left to right to each item in lx and b"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; (foldl f b (list x-1 ... x-n)) == (f x-n ... (f x-1 b))"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "foldl"
        }), " f b lx) ...)\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; factorial : NonNegInt -> PosInt"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Returns n! by generating the range [1..n] as a list, and"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; folding with the function `*`"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "factorial"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "0"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "factorial"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "factorial"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "3"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "6"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "factorial"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "6"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "720"
        }), ")\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "factorial"
        }), " n)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: "foldr"
        }), "\n    * ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), "\n    (", _jsx(_components.span, {
          className: "hljs-name",
          children: "build-list"
        }), " n add1)))\n"]
      })
    }), "\n", _jsx(_components.h3, {
      id: "local-lambda",
      children: "Local, Lambda"
    }), "\n", _jsxs(_components.p, {
      children: ["A local scope can allow you to define a function that references a function\nargument without requiring that argument as input. Using ", _jsx(_components.code, {
        children: "lambda"
      }), " or ", _jsx(_components.code, {
        children: "λ"
      }), "\n(Ctrl+\\) also creates a function in the local scope."]
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        className: "hljs language-scheme",
        children: [_jsx(_components.span, {
          className: "hljs-comment",
          children: "; usd-to-euro : [List-of Number] Number -> [List-of Number]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Converts all the currency values in the given list from USD to EUR,"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; using the given number as the EUR/USD rate"
        }), "\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "usd-to-euro"
        }), " empty ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1.1"
        }), ") empty)\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "usd-to-euro"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "4"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "6"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1.5"
        }), ") (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "3"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "6"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "9"
        }), "))\n\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "usd-to-euro"
        }), " lon rate)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: "local"
        }), " [", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; multiply-by-rate : Number -> Number"
        }), "\n          ", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Multiplies the given number by `rate`, which is passed in to `usd-to-euro`"
        }), "\n          (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "multiply-by-rate"
        }), " x)\n            (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "*"
          })
        }), " x rate))]\n    (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "map"
          })
        }), " multiply-by-rate lon)))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; alternatively"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "usd-to-euro/v2"
        }), " lon rate)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "map"
          })
        }), "\n    (", _jsx(_components.span, {
          className: "hljs-name",
          children: "λ"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "x"
        }), ") (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "*"
          })
        }), " x rate))\n    lon))\n"]
      })
    }), "\n", _jsx(_components.h3, {
      id: "multiple-complex-inputs",
      children: "Multiple complex inputs"
    }), "\n", _jsx(_components.p, {
      children: "Determine which type of processing the function will do:"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "Sequential - apply one template and then the other"
      }), "\n", _jsx(_components.li, {
        children: "Parallel - determine all situations via \"joint\" templates"
      }), "\n", _jsx(_components.li, {
        children: "Cross - use template for one, with template for other as inner call"
      }), "\n"]
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        className: "hljs language-scheme",
        children: ["(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-temp"
        }), " loa)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n   (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n     [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "empty?"
        }), " loa) ...]\n     [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "cons?"
        }), " loa)\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n       (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " loa)\n       (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-temp"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " loa)))])))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; Sequential"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; append-lists : [List-of Any] [List-of Any] -> [List-of Any]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; Reimplements `append`. The template for the second list is replaced"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; with `l2`."
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "append-lists"
        }), " empty (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ")) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "append-lists"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ") empty) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "append-lists"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"a\""
        }), ") (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ")) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"a\""
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "append-lists"
        }), " l1 l2)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "empty?"
        }), " l1) l2]\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "cons?"
        }), " l1)\n     (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cons"
          })
        }), "\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " l1)\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: "append-lists"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " l1) l2))]))\n\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; A Nat is one of:"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - 0"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; - (add1 Nat)"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; and represents a natural number"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "nat-temp"
        }), " n)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "zero?"
          })
        }), " n) ...]\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "positive?"
          })
        }), " n)\n     (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: "nat-temp"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "sub1"
        }), " n)))])))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; Parallel (example use case was my-list-ref)"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-nat-temp"
        }), " loa n)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n   (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n     [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "empty?"
        }), " loa) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "zero?"
          })
        }), " n)) ...]\n     [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "empty?"
        }), " loa) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "positive?"
          })
        }), " n))\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n       n\n       (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-nat-temp"
        }), " loa (", _jsx(_components.span, {
          className: "hljs-name",
          children: "sub1"
        }), " n)))]\n     [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "cons?"
        }), " loa) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "zero?"
          })
        }), " n))\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n       (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " loa)\n       (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-nat-temp"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " loa) n))]\n     [(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "cons?"
        }), " loa) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "positive?"
          })
        }), " n))\n      (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "..."
          })
        }), "\n       (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " loa)\n       n\n       (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-nat-temp"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " loa) (", _jsx(_components.span, {
          className: "hljs-name",
          children: "sub1"
        }), " n)))])))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: ";;; Cross"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; intersect : (X) [List-of X] [List-of X] [X X -> Boolean] -> [List-of X]"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; returns the subset of the two lists that are common given an equality relation,"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; in the order that they appear in the first list"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "intersect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "3"
        }), ") (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "4"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "5"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "6"
        }), ") =) empty)\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "intersect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), ") (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "4"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "6"
        }), ") =) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "intersect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"a\""
        }), " ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"b\""
        }), ") (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"c\""
        }), " ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"a\""
        }), ") string=?) (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-string",
          children: "\"a\""
        }), "))\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "intersect"
        }), " l1 l2 same?)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cond"
          })
        }), "\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "empty?"
        }), " l1) empty]\n    [(", _jsx(_components.span, {
          className: "hljs-name",
          children: "cons?"
        }), " l1)\n     (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "if"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-contains?"
        }), " l2 (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " l1) same?)\n         (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "cons"
          })
        }), "\n          (", _jsx(_components.span, {
          className: "hljs-name",
          children: "first"
        }), " l1)\n          (", _jsx(_components.span, {
          className: "hljs-name",
          children: "intersect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " l1) l2 same?))\n         (", _jsx(_components.span, {
          className: "hljs-name",
          children: "intersect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "rest"
        }), " l1) l2 same?))]))\n\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; list-contains? : (X) [List-of X] X [X X -> Boolean] -> Boolean"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; returns true if the given list contains the given element, according to the"
        }), "\n", _jsx(_components.span, {
          className: "hljs-comment",
          children: "; equality relation"
        }), "\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-contains?"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "2"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " =) true)\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: "check-expect"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-contains?"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "list"
          })
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "4"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "5"
        }), " ", _jsx(_components.span, {
          className: "hljs-number",
          children: "6"
        }), ") ", _jsx(_components.span, {
          className: "hljs-number",
          children: "1"
        }), " =) false)\n(", _jsx(_components.span, {
          className: "hljs-name",
          children: _jsx(_components.span, {
            className: "hljs-built_in",
            children: "define"
          })
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "list-contains?"
        }), " lox el same?)\n  (", _jsx(_components.span, {
          className: "hljs-name",
          children: "ormap"
        }), "\n   (", _jsx(_components.span, {
          className: "hljs-name",
          children: "λ"
        }), " (", _jsx(_components.span, {
          className: "hljs-name",
          children: "current-el"
        }), ") (", _jsx(_components.span, {
          className: "hljs-name",
          children: "same?"
        }), " el current-el))\n   lox))\n"]
      })
    }), "\n", _jsx(_components.h2, {
      id: "linter",
      children: "Linter"
    }), "\n", _jsxs(_components.p, {
      children: ["A tool which checks that after every signature, there is a purpose statement,\n≥2 tests which call the function named in the signature, and a function\ndefinition with the same name. It also checks that there are no function\ndefinitions without a signature, in top-level or local scopes.\n", _jsx(_components.a, {
        href: "https://github.com/dylanburati/teachlangs-lint",
        children: "View the source"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["If your code generates many warnings under one function design, make sure your\nsignatures have spaces surrounding the arrow. ", _jsx(_components.code, {
        children: "Number -> Number"
      }), " denotes 1 input\nand 1 output, but ", _jsx(_components.code, {
        children: "Number-> Number"
      }), " is read as two types called ", _jsx(_components.code, {
        children: "Number->"
      }), " and\n", _jsx(_components.code, {
        children: "Number"
      }), "."]
    }), "\n", _jsx(FundiesLinter, {})]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
